import { Box, Button, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { SectionLabel, StringListEditor } from './primitives'
import type { GroupingContent, MatchingContent, OrderingContent } from '../contentTypes'

/**
 * Редакторы типов на порядок и соответствие: пары, последовательность и
 * распределение по группам.
 */

type EditorProps<T> = {
  content: T
  onChange: (content: T) => void
  disabled?: boolean
}

/**
 * Индексы повторов в колонке пар.
 *
 * Сравнение как на сервере — без регистра и лишних пробелов: там пара
 * опознаётся текстом, поэтому «Кошка» и «кошка» для проверки одно и то же, и
 * такое задание сервер не примет. Показываем это учителю сразу в форме, а не
 * отказом на сохранении.
 */
function duplicateIndexes(values: string[]): Set<number> {
  const seen = new Map<string, number>()
  const duplicates = new Set<number>()

  values.forEach((value, index) => {
    const key = value.trim().replace(/\s+/g, ' ').toLowerCase()

    if (key === '') return

    const first = seen.get(key)

    if (first === undefined) {
      seen.set(key, index)

      return
    }

    duplicates.add(first)
    duplicates.add(index)
  })

  return duplicates
}

export function MatchingEditor({ content, onChange, disabled }: EditorProps<MatchingContent>) {
  const { t } = useTranslation()

  const duplicateLefts = duplicateIndexes(content.pairs.map((pair) => pair.left))
  const duplicateRights = duplicateIndexes(content.pairs.map((pair) => pair.right ?? ''))

  return (
    <Box>
      <SectionLabel text={t('exercises.editor.pairs')} hint={t('exercises.editor.pairsHint')} />
      <Stack spacing={1}>
        {content.pairs.map((pair, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="center">
            <TextField
              fullWidth
              size="small"
              label={t('exercises.editor.pairLeft')}
              value={pair.left}
              disabled={disabled}
              error={duplicateLefts.has(index)}
              helperText={duplicateLefts.has(index) ? t('exercises.editor.pairDuplicateLeft') : undefined}
              onChange={(event) => {
                const pairs = [...content.pairs]
                pairs[index] = { ...pair, left: event.target.value }
                onChange({ pairs })
              }}
            />
            <TextField
              fullWidth
              size="small"
              label={t('exercises.editor.pairRight')}
              value={pair.right}
              disabled={disabled}
              error={duplicateRights.has(index)}
              helperText={duplicateRights.has(index) ? t('exercises.editor.pairDuplicateRight') : undefined}
              onChange={(event) => {
                const pairs = [...content.pairs]
                pairs[index] = { ...pair, right: event.target.value }
                onChange({ pairs })
              }}
            />
            <IconButton
              size="small"
              disabled={disabled || content.pairs.length <= 2}
              onClick={() => onChange({ pairs: content.pairs.filter((_, i) => i !== index) })}
              aria-label={t('common.delete')}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={disabled}
            onClick={() => onChange({ pairs: [...content.pairs, { left: '', right: '' }] })}
          >
            {t('exercises.editor.addPair')}
          </Button>
        </Box>
      </Stack>
    </Box>
  )
}

export function OrderingEditor({ content, onChange, disabled }: EditorProps<OrderingContent>) {
  const { t } = useTranslation()
  // Учителю элементы приходят строками в правильном порядке; форма ученика
  // (объекты с исходным индексом) в конструкторе не встречается.
  const items = (content.items as unknown[]).map((item) =>
    typeof item === 'string' ? item : String((item as { text?: string }).text ?? ''),
  )

  return (
    <Stack spacing={1}>
      <StringListEditor
        label={t('exercises.editor.orderedItems')}
        values={items}
        onChange={(next) => onChange({ items: next })}
        minItems={2}
        addLabel={t('exercises.editor.addItem')}
        disabled={disabled}
      />
      <Typography variant="caption" color="text.secondary">
        {t('exercises.editor.orderingHint')}
      </Typography>
    </Stack>
  )
}

export function GroupingEditor({ content, onChange, disabled }: EditorProps<GroupingContent>) {
  const { t } = useTranslation()

  const removeGroup = (index: number) => {
    const groups = content.groups.filter((_, i) => i !== index)
    // Элементы удалённой группы переезжают в первую: ссылка на несуществующую
    // группу отклоняется сервером, а молча терять элементы нельзя.
    const items = content.items.map((item) => {
      const group = item.group ?? 0
      if (group === index) return { ...item, group: 0 }
      return group > index ? { ...item, group: group - 1 } : item
    })
    onChange({ groups, items })
  }

  return (
    <Stack spacing={2}>
      <Box>
        <SectionLabel text={t('exercises.editor.groups')} />
        <Stack spacing={1}>
          {content.groups.map((group, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              <TextField
                fullWidth
                size="small"
                value={group}
                disabled={disabled}
                onChange={(event) => {
                  const groups = [...content.groups]
                  groups[index] = event.target.value
                  onChange({ ...content, groups })
                }}
              />
              <IconButton
                size="small"
                disabled={disabled || content.groups.length <= 2}
                onClick={() => removeGroup(index)}
                aria-label={t('common.delete')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={disabled}
              onClick={() => onChange({ ...content, groups: [...content.groups, ''] })}
            >
              {t('exercises.editor.addGroup')}
            </Button>
          </Box>
        </Stack>
      </Box>

      <Box>
        <SectionLabel text={t('exercises.editor.groupedItems')} />
        <Stack spacing={1}>
          {content.items.map((item, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              <TextField
                fullWidth
                size="small"
                value={item.text}
                disabled={disabled}
                onChange={(event) => {
                  const items = [...content.items]
                  items[index] = { ...item, text: event.target.value }
                  onChange({ ...content, items })
                }}
              />
              <TextField
                select
                size="small"
                sx={{ minWidth: 180 }}
                label={t('exercises.editor.group')}
                value={item.group ?? 0}
                disabled={disabled}
                onChange={(event) => {
                  const items = [...content.items]
                  items[index] = { ...item, group: Number(event.target.value) }
                  onChange({ ...content, items })
                }}
              >
                {content.groups.map((group, groupIndex) => (
                  <MenuItem key={groupIndex} value={groupIndex}>
                    {group || t('exercises.editor.groupNumber', { number: groupIndex + 1 })}
                  </MenuItem>
                ))}
              </TextField>
              <IconButton
                size="small"
                disabled={disabled || content.items.length <= 2}
                onClick={() => onChange({ ...content, items: content.items.filter((_, i) => i !== index) })}
                aria-label={t('common.delete')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={disabled}
              onClick={() => onChange({ ...content, items: [...content.items, { text: '', group: 0 }] })}
            >
              {t('exercises.editor.addItem')}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Stack>
  )
}
